import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import { CmLongLoading, CmScrollBox } from '@/components/uiParts';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { CanNotUpdateMenuManagementNotice } from '@/pages/menu-managements/components/Alert/CanNotUpdateMenuManagementNotice';
import { SearchItemSelectModal } from '@/pages/menu-managements/components/SearchItemSelectModal';
import { MenuManagementEditButtonArea } from '@/pages/menu-managements/components/TopButtonArea';
import { MenuManagementOptionGroupsForm } from '@/pages/menu-managements/option-groups/components/Form';
import { useMenuManagementOptionGroupForm } from '@/pages/menu-managements/option-groups/hooks/useMenuManagementOptionGroupForm';
import { routes } from '@/router';
import { useMenuManagementDeleteBrandOptionGroup } from '@/services/BrandOptionGroup/hooks/delete/useMenuManagementDeleteBrandOptionGroup';

const MenuManagementOptionGroupsEditPage: React.FC = (): JSX.Element => {
  const { id: urlParamsId } = useParams<{ id: string }>();
  const history = useHistory();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    associateBrandSelectInput,
    titleJa,
    quantity,
    memo,
    takeout,
    eatIn,
    delivery,
    error,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    initialStringifyFormState,
    isLoading,
    isOptionGroupItemModalOpen,
    setIsLoading,
    handleUpdate,
    handleInputTitleJa,
    handleInputMemo,
    handleInputTakeoutPosCode,
    handleInputEatInPosCode,
    handleInputDeliveryPosCode,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    handleInputQuantityMin,
    handleInputQuantityMax,
    handleInputQuantityMaxSameOption,
    initializeEditPageState,
    handleClickMultipleItemSelect,
    handleCloseOptionGroupItemModal,
    handleSetMultipleItems,
  } = useMenuManagementOptionGroupForm();

  const handleDelete = () => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementDeleteBrandOptionGroup(urlParamsId);
        setIsLoading(false);
        alert('オプショングループを削除しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  const isEdit =
    JSON.stringify([
      titleJa,
      memo,
      takeout,
      eatIn,
      delivery,
      selectedItems,
      quantity,
    ]) !== initialStringifyFormState;
  const isDisabledSubmitButton = [
    error.titleJa.isError,
    error.memo.isError,
    error.quantity.min.isError,
    error.quantity.max.isError,
    error.quantity.maxSameOption.isError,
    !isEdit,
  ].some((error) => error);

  const listUrl = routes.menuManagementOptionGroups.path;

  useEffect(() => {
    (async () => {
      await initializeEditPageState(urlParamsId);
    })();
  }, [urlParamsId]);

  return (
    <PageLayout
      title={routes.menuManagementOptionGroupsEdit.title}
      associateBrandSelectInput={{
        ...associateBrandSelectInput,
        isDisabled: true,
      }}
      alert={
        <CanNotUpdateMenuManagementNotice pageName={'オプショングループ'} />
      }
    >
      <MenuManagementEditButtonArea
        name="オプショングループ"
        isOpenDeleteOpen={isOpen}
        handleOpenDeleteModal={onOpen}
        handleCloseDeleteModal={onClose}
        {...{
          listUrl,
          urlParamsId,
          isDisabledSubmitButton,
          handleUpdate,
          handleDelete,
        }}
      />
      <CmScrollBox height={`calc(100vh - ${targetsHeight}px)`}>
        <MenuManagementOptionGroupsForm
          handleClickMultipleSelect={handleClickMultipleItemSelect}
          {...{
            associateBrandSelectInput,
            titleJa,
            memo,
            takeout,
            eatIn,
            delivery,
            quantity,
            handleInputTitleJa,
            handleInputMemo,
            handleInputTakeoutPosCode,
            handleInputEatInPosCode,
            handleInputDeliveryPosCode,
            error,
            handleInputQuantityMin,
            handleInputQuantityMax,
            handleInputQuantityMaxSameOption,
            searchItems,
            selectedItems,
            handleOnDragEnd,
            searchItemName,
            handleSearchItemNameInput,
            handleAddSelectItems,
            handleDeleteDragItem,
            isSearching,
          }}
          brandSelectValue={associateBrandSelectInput.value?.value ?? ''}
        />
      </CmScrollBox>
      {isLoading && <CmLongLoading />}
      <SearchItemSelectModal
        title="オプション商品の選択"
        emptyMessage="選択できるオプション商品がありませんでした"
        isLoading={isSearching}
        isOpen={isOptionGroupItemModalOpen}
        onClose={handleCloseOptionGroupItemModal}
        selectedItems={selectedItems}
        searchItems={searchItems}
        handleSetMultipleItems={handleSetMultipleItems}
      />
    </PageLayout>
  );
};

export default MenuManagementOptionGroupsEditPage;
